<script setup lang="ts">
import { KsButton, KsDropdown } from "@aschehoug/kloss";

const { public: env } = useRuntimeConfig();

const cards = useArticleCards();

await cards.suspense();

const optionsSelectedSchool: Ref<string | null> = ref(null);
const optionsSelectedSubject: Ref<string | null> = ref(null);
const optionsSelectedTheme: Ref<string | null> = ref(null);

const PAGE_SIZE: number = 12;

const currentSlice = ref(PAGE_SIZE);

type Predicate = (arr: ArticleCard[]) => ArticleCard[];

const PAGINATION_PREDICATE: Predicate = (arr) =>
  arr.slice(0, currentSlice.value);

const predicate = ref(PAGINATION_PREDICATE);

const slicedArticles = computed(() => predicate.value(cards.data.value ?? []));

const hasMoreArticles = computed(
  () =>
    predicate.value === PAGINATION_PREDICATE &&
    slicedArticles.value.length < (cards.data.value?.length ?? 0),
);

function loadMore() {
  currentSlice.value += PAGE_SIZE;
}

function handleOption() {
  const { value: selectedSchool } = optionsSelectedSchool;
  const { value: selectedSubject } = optionsSelectedSubject;
  const { value: selectedTheme } = optionsSelectedTheme;
  const hasSelectedSchool = selectedSchool !== null;
  const hasSelectedSubject = selectedSubject !== null;
  const hasSelectedTheme = selectedTheme !== null;

  if (hasSelectedSchool || hasSelectedSubject || hasSelectedTheme) {
    predicate.value = (cards) =>
      cards.filter((article) => {
        if (hasSelectedSchool && hasSelectedSubject && hasSelectedTheme) {
          return (
            article?.levels.includes(selectedSchool as Level) &&
            article?.subject?.indexOf(selectedSubject) === 0 &&
            article?.theme?.indexOf(selectedTheme) === 0
          );
        } else if (hasSelectedSchool && hasSelectedSubject) {
          return (
            article?.levels.includes(selectedSchool as Level) &&
            article?.subject?.indexOf(selectedSubject) === 0
          );
        } else if (hasSelectedSchool && hasSelectedTheme) {
          return (
            article?.levels.includes(selectedSchool as Level) &&
            article?.theme?.indexOf(selectedTheme) === 0
          );
        } else if (hasSelectedSubject && hasSelectedTheme) {
          return (
            article?.subject?.indexOf(selectedSubject) === 0 &&
            article?.theme?.indexOf(selectedTheme) === 0
          );
        } else if (hasSelectedSchool) {
          return article?.levels.includes(selectedSchool as Level);
        } else if (hasSelectedSubject) {
          return article?.subject?.indexOf(selectedSubject) === 0;
        } else if (hasSelectedTheme) {
          return article?.theme?.indexOf(selectedTheme) === 0;
        } else {
          return false;
        }
      });

    currentSlice.value = slicedArticles.value.length;
  } else {
    predicate.value = PAGINATION_PREDICATE;
    currentSlice.value = slicedArticles.value.length;
  }
}
</script>

<template>
  <main id="main-content" class="pt-16">
    <Content class="text-center">
      <h1 id="articles-card-title" class="mb-4 text-base uppercase text-green-45">
        Artikler
      </h1>
      <h2 class="headline mb-16 text-5xl text-green-60">
        Inspirasjon til undervisning
      </h2>
      <div class="mb-20 flex flex-wrap justify-center gap-4 [--ks-secondary:theme(colors.green.60)]">
        <KsDropdown
          v-model="optionsSelectedSchool"
          :options="[...LEVEL_OPTIONS]"
          placeholder="Velg trinn"
          no-options="Ingen trinn!"
          shape="rounded"
          autolabel
          :close-on-select="true"
          :option-label="(option: Option) => `${option.title}`"
          :selectable="(option: Option) => option.title !== 'skip'"
          :reduce="(option: Option) => option.value"
          @close="handleOption"
        />
        <KsDropdown
          v-model="optionsSelectedSubject"
          :options="[...SUBJECT_OPTIONS]"
          placeholder="Velg fag"
          no-options="Ingen fag!"
          shape="rounded"
          autolabel
          :close-on-select="true"
          :option-label="(option: Option) => `${option.title}`"
          :selectable="(option: Option) => option.title !== 'skip'"
          :reduce="(option: Option) => option.value"
          @close="handleOption"
        />
        <KsDropdown
          v-model="optionsSelectedTheme"
          :options="[...THEME_OPTIONS]"
          placeholder="Velg tema"
          no-options="Ingen tema!"
          shape="rounded"
          autolabel
          :close-on-select="true"
          :option-label="(option: Option) => `${option.title}`"
          :selectable="(option: Option) => option.title !== 'skip'"
          :reduce="(option: Option) => option.value"
          @close="handleOption"
        />
      </div>
      <section
        id="articles-cards"
        aria-labelledby="articles-card-title"
        class="mb-16 flex flex-wrap justify-center gap-10"
      >
        <CardsArticle
          v-for="article in slicedArticles"
          :key="article.id"
          :article="article"
        />
        <NoResults v-if="slicedArticles.length === 0">
          Prøv å endre filtrering for å finne inspirerende artikler
        </NoResults>
      </section>
      <KsButton
        variant="primary"
        size="large"
        shape="rounded"
        class="mb-32"
        :class="{ '!hidden': !hasMoreArticles }"
        @click="loadMore"
      >
        Vis mer
      </KsButton>
    </Content>
    <CtaNewsletter />
  </main>
</template>
